"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const number_utils_1 = require("./number-utils");
/**
 * The formula for calculating fees and costs is as follows:
 *
 *   Nurse pay rate * Platform fee * Employer fee = Total rate
 *
 * Given a nurse gross rate of $50, a platform fee of 5%, and an employer fee of 10%:
 *
 *   50 * 1.05 * 1.1 = 57.75 Total Rate
 *
 * We work backwards from the total rate to determine the pay rate because the total
 * rate is what is entered by the facility:
 *
 *   Total rate / Platform fee / Employer fee = Nurse pay rate
 *
 *   57.75 / 1.05 / 1.1 = 50 Nurse pay rate
 *
 * All monetary numbers in this class are in minor units (pennies).
 */
class CostCalculator {
    inputRate;
    platformFeePct;
    employerFeePct;
    minutesWorked;
    stipend;
    bonus;
    constructor({ inputRate, platformFeePct, employerFeePct, minutesWorked, stipend = 0, bonus = 0, }) {
        this.inputRate = inputRate;
        this.platformFeePct = platformFeePct;
        this.employerFeePct = employerFeePct;
        this.minutesWorked = minutesWorked;
        this.stipend = stipend;
        this.bonus = bonus;
    }
    get payRate() {
        return this.inputRate - (this.platformFeeRate + this.employerFeeRate);
    }
    get platformFeeRate() {
        return (this.inputRate -
            Math.round(this.inputRate / (1 + this.platformFeePct)));
    }
    get employerFeeRate() {
        const minusPlatformFee = this.inputRate - this.platformFeeRate;
        return (minusPlatformFee -
            Math.round(minusPlatformFee / (1 + this.employerFeePct)));
    }
    get totalRate() {
        return this.payRate + this.platformFeeRate + this.employerFeeRate;
    }
    get totalPay() {
        return (Math.round(this.payRate * this.hoursWorked) +
            this.stipend +
            this.bonus);
    }
    get totalPlatformFee() {
        return (Math.round(this.platformFeeRate * this.hoursWorked) +
            this.bonusPlatformFee);
    }
    get totalEmployerFee() {
        return (Math.round(this.employerFeeRate * this.hoursWorked) +
            this.bonusEmployerFee);
    }
    get bonusEmployerFee() {
        return this.bonusCosts.employerFee;
    }
    get totalBonusCost() {
        return this.bonusCosts.total;
    }
    get bonusPlatformFee() {
        return this.bonusCosts.platformFee;
    }
    get bonusCosts() {
        return CostCalculator.getBonusCosts({
            bonus: this.bonus,
            platformFeePct: this.platformFeePct,
            employerFeePct: this.employerFeePct,
        });
    }
    get totalCost() {
        return this.totalPay + this.totalPlatformFee + this.totalEmployerFee;
    }
    get hoursWorked() {
        if (!this.minutesWorked) {
            throw new Error('Minutes worked must be greater than zero to calculate hours worked');
        }
        // ! round this only to two decimal places
        return (0, number_utils_1.roundToAtMostTwoDecimals)(this.minutesWorked / 60);
    }
    /**
     * Get all costs per hour.
     *
     * @param totalRate The $ rate, in pennies, as posted by the facility.
     * @param platformFeePct The percentage of base rate to charge for platform fee. Should be between 0.01 and 1.0.
     * @param employerFeePct The percentage of base rate to charge for employer fee. Should be between 0.01 and 1.0.
     * @returns {CalcualtedRates} Object of per hour costs.
     */
    static getCostsPerHour(totalRate, platformFeePct, employerFeePct) {
        const calc = new CostCalculator({
            inputRate: totalRate,
            platformFeePct,
            employerFeePct,
        });
        return {
            payRate: calc.payRate,
            platformFeeRate: calc.platformFeeRate,
            employerFeeRate: calc.employerFeeRate,
            totalRate: calc.totalRate,
        };
    }
    /**
     * Returns individual costs so they can be flexibly arranged on the invoice.
     *
     * @returns {CalculatedCosts} Object of total costs
     */
    static getTotalCosts(options) {
        const calc = new CostCalculator(options);
        return {
            pay: calc.totalPay,
            platformFee: calc.totalPlatformFee,
            employerFee: calc.totalEmployerFee,
            stipend: calc.stipend,
            bonus: calc.bonus,
            bonusCost: calc.totalBonusCost,
            total: calc.totalCost,
        };
    }
    /**
     * A bonus is entered as nurse pay, so its fees are derived forwards, in the
     * same nesting the backward math above implies: the employer fee is charged
     * on the pay, and the platform fee on the pay plus that employer fee.
     *
     * @returns {BonusCosts} What one bonus costs the facility.
     */
    static getBonusCosts({ bonus, platformFeePct, employerFeePct, }) {
        const employerFee = Math.round(bonus * employerFeePct);
        const platformFee = Math.round((bonus + employerFee) * platformFeePct);
        return {
            bonus,
            employerFee,
            platformFee,
            total: bonus + employerFee + platformFee,
        };
    }
    /**
     * Fees are rounded per bonus, the way each one is billed, rather than on
     * the sum, so this agrees to the penny with what an invoice charges.
     *
     * @returns {BonusCosts} What every bonus on a shift costs the facility.
     */
    static getTotalBonusCosts({ bonuses, platformFeePct, employerFeePct, }) {
        return bonuses
            .map(({ amount }) => CostCalculator.getBonusCosts({
            bonus: amount,
            platformFeePct,
            employerFeePct,
        }))
            .reduce((running, costs) => ({
            bonus: running.bonus + costs.bonus,
            employerFee: running.employerFee + costs.employerFee,
            platformFee: running.platformFee + costs.platformFee,
            total: running.total + costs.total,
        }), { bonus: 0, employerFee: 0, platformFee: 0, total: 0 });
    }
    static getTotalRateFromPayRate(payRate, platformFeePct, employerFeePct) {
        return payRate * (1 + platformFeePct) * (1 + employerFeePct);
    }
}
exports.default = CostCalculator;
//# sourceMappingURL=cost-calculator.js.map